import React from 'react';
import {DataGrid} from '@mui/x-data-grid';

// Suppress ResizeObserver loop errors (non-critical warnings)
const originalConsoleError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
    return; // Suppress this specific error
  }
  originalConsoleError.apply(console, args);
};
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  NewReleases as NewIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import IntegrationSourceChip from 'components/IntegrationSourceChip';

export default function RowEditControlGrid({
  dataToShow,
  setPageSize,
  pageSize,
  setPageNumber,
  pageNumber,
  rowCount,
  onEditCommit,
  setPatchEdited,
  gettingData,
  handleOnSelect,
  userRole,
  formatDate, // Pass formatDate here
  onOpenLeadHistory,
}) {
  const [editRowsModel, setEditRowsModel] = React.useState({});

  // State for edit modal
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editFormData, setEditFormData] = React.useState({});

  // State for other data modal
  const [otherDataModalOpen, setOtherDataModalOpen] = React.useState(false);
  const [otherDataContent, setOtherDataContent] = React.useState(null);

  // Handler for showing other data modal
  const handleShowOtherData = (otherData) => {
    setOtherDataContent(otherData);
    setOtherDataModalOpen(true);
  };

  // Handler for closing other data modal
  const handleCloseOtherDataModal = () => {
    setOtherDataModalOpen(false);
    setOtherDataContent(null);
  };

  // Function to check if a lead is new (uses 24-hour rule with created_at)
  const isNewLead = (isNew, created_at) => {
    console.log('🔍 isNewLead called with:', {isNew, created_at});
    
    if (created_at) {
      const createdDate = new Date(created_at);
      const now = new Date();
      const hoursDiff = (now - createdDate) / (1000 * 60 * 60);
      const isInFuture = createdDate > now;
      
      console.log('🔍 24-hour check:', {
        created_at,
        createdDate: createdDate.toISOString(),
        now: now.toISOString(),
        hoursDiff: hoursDiff.toFixed(2),
        isInFuture,
        result: !isInFuture && hoursDiff >= 0 && hoursDiff <= 24
      });
      
      // Only show NEW chip if data is in the past AND within last 24 hours
      return !isInFuture && hoursDiff >= 0 && hoursDiff <= 24;
    }
    
    // If no created_at, don't show NEW chip
    return false;
  };

  // Function to check if a lead was updated in the last 24 hours
  const isUpdatedLead = (updated_at) => {
    console.log('🔍 isUpdatedLead called with:', {updated_at});
    
    if (updated_at) {
      const updatedDate = new Date(updated_at);
      const now = new Date();
      const hoursDiff = (now - updatedDate) / (1000 * 60 * 60);
      const isInFuture = updatedDate > now;
      
      console.log('🔍 Updated 24-hour check:', {
        updated_at,
        updatedDate: updatedDate.toISOString(),
        now: now.toISOString(),
        hoursDiff: hoursDiff.toFixed(2),
        isInFuture,
        result: !isInFuture && hoursDiff >= 0 && hoursDiff <= 24
      });
      
      // Only show UPDATED chip if data was updated in the past AND within last 24 hours
      return !isInFuture && hoursDiff >= 0 && hoursDiff <= 24;
    }
    
    // If no updated_at, don't show UPDATED chip
    return false;
  };

  // Skeleton loading component
  const SkeletonDataGrid = () => (
    <Box sx={{width: '100%'}}>
      {/* Toolbar skeleton */}
      <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Box display='flex' gap={1}>
            <Skeleton variant='rectangular' width={120} height={36} />
            <Skeleton variant='rectangular' width={100} height={36} />
            <Skeleton variant='rectangular' width={100} height={36} />
            <Skeleton variant='rectangular' width={100} height={36} />
          </Box>
          <Box display='flex' gap={1}>
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='circular' width={32} height={32} />
          </Box>
        </Box>
      </Box>

      {/* Table skeleton */}
      <Box sx={{p: 2}}>
        {/* Header row */}
        <Box display='flex' gap={1} mb={1}>
          <Skeleton variant='rectangular' width={40} height={24} />
          <Skeleton variant='text' width={120} height={24} />
          <Skeleton variant='text' width={150} height={24} />
          <Skeleton variant='text' width={100} height={24} />
          <Skeleton variant='text' width={80} height={24} />
          <Skeleton variant='text' width={100} height={24} />
          <Skeleton variant='text' width={80} height={24} />
          <Skeleton variant='text' width={100} height={24} />
          <Skeleton variant='text' width={80} height={24} />
          <Skeleton variant='text' width={60} height={24} />
        </Box>

        {/* Data rows */}
        {Array.from({length: 8}).map((_, index) => (
          <Box key={index} display='flex' gap={1} mb={1} alignItems='center'>
            <Skeleton variant='rectangular' width={40} height={24} />
            <Skeleton variant='text' width={120} height={24} />
            <Skeleton variant='text' width={150} height={24} />
            <Skeleton variant='text' width={100} height={24} />
            <Skeleton variant='text' width={80} height={24} />
            <Skeleton variant='text' width={100} height={24} />
            <Skeleton variant='text' width={80} height={24} />
            <Skeleton variant='text' width={100} height={24} />
            <Skeleton variant='text' width={80} height={24} />
            <Box display='flex' gap={0.5}>
              <Skeleton variant='circular' width={24} height={24} />
              <Skeleton variant='circular' width={24} height={24} />
              <Skeleton variant='circular' width={24} height={24} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // Handle double-click to open edit modal
  const handleRowDoubleClick = (params) => {
    const rowData = params.row;
    setEditingRow(rowData);
    setEditFormData({...rowData});
    setEditModalOpen(true);
  };

  // Handle edit form data changes
  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle save edit
  const handleSaveEdit = () => {
    if (onEditCommit && editingRow) {
      // Create the patchEdited object structure expected by onEditCommit
      const updatedPatchEdited = {
        [editingRow.id]: {},
      };

      // Convert editFormData to the expected format
      Object.keys(editFormData).forEach((key) => {
        if (
          key !== 'id' &&
          key !== 'created_at' &&
          key !== 'updated_at' &&
          key !== 'Rowkey'
        ) {
          let value = editFormData[key];

          // Try to parse other_data as JSON if it looks like JSON
          if (key === 'other_data' && value) {
            try {
              const trimmedValue = value.trim();
              if (
                trimmedValue.startsWith('{') ||
                trimmedValue.startsWith('[')
              ) {
                value = JSON.parse(trimmedValue);
              }
            } catch (e) {
              // Keep as string if JSON parse fails
              console.log('other_data is not valid JSON, keeping as string');
            }
          }

          updatedPatchEdited[editingRow.id][key] = {value: value};
        }
      });

      // Update the patchEdited state
      setPatchEdited((prev) => ({
        ...prev,
        ...updatedPatchEdited,
      }));

      // Call onEditCommit with the updated data directly
      onEditCommit(editingRow.id, updatedPatchEdited);
    }
    setEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({});
  };

  const handleEditRowsModelChange = React.useCallback(
    (model) => {
      setEditRowsModel(model);
      // Only update patchEdited if model is valid
      if (model && typeof model === 'object') {
        setPatchEdited(model);
      } else {
        setPatchEdited({});
      }
    },
    [setPatchEdited],
  );

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {userRole.includes('admin') && (
          <GridToolbarExport csvOptions={{utf8WithBom: true}} />
        )}
      </GridToolbarContainer>
    );
  }

  // Show skeleton loading when getting data and no data to show
  if (gettingData && dataToShow.length === 0) {
    return <SkeletonDataGrid />;
  }

  return (
    <>
      <div style={{width: '100%'}}>
        <div style={{width: '100%'}}>
          <DataGrid
            autoHeight
            loading={false} // Disable default loading since we're using skeleton
            checkboxSelection
            disableVirtualization={false} // Keep virtualization for performance
            rowHeight={52} // Fixed row height to reduce layout shifts
            headerHeight={56} // Fixed header height
            rows={dataToShow.map((row) => {
              const isNew = isNewLead(row.is_new, row.created_at);
              const isUpdated = isUpdatedLead(row.updated_at);
              console.log('🔍 Row mapping:', {
                id: row.id,
                created_at: row.created_at,
                updated_at: row.updated_at,
                isNew,
                isUpdated
              });
              
              // Debug: Check if is_updated column will be visible
              if (isUpdated) {
                console.log('🔍 UPDATED chip should show for row:', row.id, 'isUpdated:', isUpdated);
              }
              return {
                ...row,
                api_success:
                  row.integration_source && row.api_credential_id
                    ? 'success'
                    : null,
                is_new: isNew,
                is_updated: isUpdated,
              };
            })}
            onSelectionModelChange={handleOnSelect}
            onRowDoubleClick={handleRowDoubleClick}
            rowCount={rowCount}
            columns={(() => {
              // Helper function to get column width
              function getColumnWidth(key) {
                const widthMap = {
                  id: 80,
                  fullName: 200,
                  email: 200,
                  phone: 150,
                  data_source: 150,
                  integration_source: 180,
                  api_success: 120,
                  is_new: 100,
                  is_updated: 100,
                  age: 80,
                  country: 120,
                  marks: 100,
                  MPC_4: 100,
                  created_at: 150,
                  updated_at: 150,
                };
                return widthMap[key] || 150;
              }

              // Helper function to get minimum width
              function getMinWidth(key) {
                const minWidthMap = {
                  id: 60,
                  is_new: 80,
                  is_updated: 80,
                  age: 60,
                  marks: 80,
                  MPC_4: 80,
                };
                return minWidthMap[key] || 100;
              }

              // Create a sample row that includes our computed fields
              const sampleRow = dataToShow[0] ? {
                ...dataToShow[0],
                is_new: isNewLead(dataToShow[0].is_new, dataToShow[0].created_at),
                is_updated: isUpdatedLead(dataToShow[0].updated_at),
              } : {};
              
              const extendedSampleRow = sampleRow;

              const columns = Object.keys(extendedSampleRow)
                    .filter(
                      (key) =>
                        key !== 'Rowkey' &&
                        key !== 'api_success' &&
                        key !== 'formId' &&
                        key !== 'duplicate_count' &&
                        key !== 'duplicate_ids' &&
                        key !== 'is_duplicate_of_sent' &&
                        key !== 'current_data_id' &&
                        key !== 'original_data_id' &&
                        key !== 'original_data_id',
                        // key !== 'lead_history_count', // Unhide lead_history_count
                    ) // Hide unwanted columns
                    .sort((a, b) => {
                      // Custom sort to match the report column order
                      const order = [
                        'id', // Move id to the first position
                        'lead_history_count', // Move Lead History to second position
                        'is_new',
                        'is_updated',
                        'fullName',
                        'phone',
                        'age',
                        'email',
                        'country',
                        'marks',
                        'MPC_4',
                        'data_source',
                        'other_data',
                        'integration_source',
                        'created_at',
                        'updated_at',
                        'error', // Move error to the very end
                      ];
                      const aIndex = order.indexOf(a);
                      const bIndex = order.indexOf(b);
                      if (aIndex !== -1 && bIndex !== -1)
                        return aIndex - bIndex;
                      if (aIndex !== -1) return -1;
                      if (bIndex !== -1) return 1;
                      return 0;
                    })
                    .map((key) => {
                      const baseColumn = {
                        field: key,
                        headerName: key.replace(/_/g, ' ').toUpperCase(),
                        editable:
                          key !== 'id' &&
                          key !== 'created_at' &&
                          key !== 'updated_at' &&
                          key !== 'integration_source' &&
                          key !== 'other_data',
                        width: getColumnWidth(key),
                        minWidth: getMinWidth(key),
                        valueGetter: (params) => {
                          if (
                            ['created_at', 'updated_at', 'sent_at'].includes(
                              key,
                            )
                          ) {
                            return formatDate
                              ? formatDate(params.value)
                              : params.value;
                          }
                          // Format other_data to show as string instead of [object]
                          if (key === 'other_data' && params.value) {
                            if (typeof params.value === 'object') {
                              return JSON.stringify(params.value);
                            }
                          }
                          return params.value;
                        },
                      };

                      // Add special rendering for specific columns
                      if (key === 'is_new') {
                        return {
                          ...baseColumn,
                          headerName: 'NEW',
                          renderCell: (params) => {
                            // Check if data is new based on 8-day rule using created_at
                            const isDataNew = (() => {
                              if (!params.row.created_at) return false;
                              
                              const createdDate = new Date(params.row.created_at);
                              const now = new Date();
                              const hoursDiff = (now - createdDate) / (1000 * 60 * 60);
                              const isInFuture = createdDate > now;
                              
                              // Show NEW chip if data is in the past AND within last 8 days (192 hours)
                              return !isInFuture && hoursDiff >= 0 && hoursDiff <= 192;
                            })();
                            
                            return isDataNew ? (
                              <Chip
                                icon={<NewIcon />}
                                label="NEW"
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  color: 'white',
                                  '& .MuiChip-icon': {
                                    fontSize: '0.8rem',
                                    color: 'white',
                                  },
                                  '& .MuiChip-label': {
                                    color: 'white',
                                  },
                                }}
                              />
                            ) : (
                              <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <span style={{ color: '#ccc' }}>—</span>
                              </Box>
                            );
                          },
                        };
                      }

                      if (key === 'is_updated') {
                        console.log('🔍 Rendering is_updated column for key:', key);
                        return {
                          ...baseColumn,
                          headerName: 'UPDATED',
                          renderCell: (params) => {
                            console.log('🔍 is_updated renderCell called with params.value:', params.value);
                            return params.value ? (
                              <Chip
                                icon={<NewIcon />}
                                label="UPDATED"
                                size="small"
                                color="error"
                                variant="filled"
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  color: 'white',
                                  '& .MuiChip-icon': {
                                    fontSize: '0.8rem',
                                    color: 'white',
                                  },
                                  '& .MuiChip-label': {
                                    color: 'white',
                                  },
                                }}
                              />
                            ) : (
                              <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <span style={{ color: '#ccc' }}>—</span>
                              </Box>
                            );
                          },
                        };
                      }

                      if (key === 'integration_source') {
                        return {
                          ...baseColumn,
                          renderCell: (params) => (
                            <IntegrationSourceChip
                              integration_source={params.value}
                            />
                          ),
                        };
                      }

                      if (key === 'data_source') {
                        return {
                          ...baseColumn,
                          renderCell: (params) => (
                            <Chip
                              label={params.value || 'Unknown'}
                              size='small'
                              variant='outlined'
                              sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                fontSize: '0.75rem',
                                height: '24px',
                              }}
                            />
                          ),
                        };
                      }

                      if (key === 'other_data') {
                        return {
                          ...baseColumn,
                          width: 120,
                          renderCell: (params) => (
                            params.value ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleShowOtherData(params.value)}
                                sx={{
                                  fontSize: '0.7rem',
                                  height: '24px',
                                  minWidth: '60px',
                                }}
                              >
                                Show
                              </Button>
                            ) : (
                              <span style={{ color: '#ccc' }}>—</span>
                            )
                          ),
                        };
                      }

                      if (key === 'lead_history_count') {
                        return {
                          ...baseColumn,
                          renderCell: (params) => {
                            const count = Number(params.value || 0);
                            const title = count > 0 ? `${count} failure${count === 1 ? '' : 's'}` : 'No failures';
                            return (
                              <Tooltip title={title}>
                                <span>
                                  {count > 0 && (
                                    <Chip
                                      label={`×${count}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: '22px' }}
                                    />
                                  )}
                                  <IconButton
                                    size="small"
                                    color={count > 0 ? 'error' : 'default'}
                                    onClick={() => onOpenLeadHistory && onOpenLeadHistory(params.row)}
                                    sx={{ ml: count > 0 ? 0.5 : 0 }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          }
                        };
                      }

                      return baseColumn;
                    });

              return columns;
            })()}
            editRowsModel={editRowsModel}
            editMode='row'
            disableSelectionOnClick
            components={{
              Toolbar: CustomToolbar,
            }}
            onEditRowsModelChange={handleEditRowsModelChange}
            page={pageNumber}
            pageSize={pageSize}
            onPageChange={setPageNumber}
            onPageSizeChange={setPageSize}
            paginationMode='server'
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowEditCommit={(params) => {
              // Don't call onEditCommit immediately, just store the changes
              console.log('Row edit committed:', params);
            }}
          />
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCancelEdit}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <EditIcon />
            Edit Data Entry
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            {editingRow &&
              Object.keys(editFormData).map((key) => {
                // Hide these fields from the edit modal
                if (
                  key === 'id' ||
                  key === 'created_at' ||
                  key === 'updated_at' ||
                  key === 'Rowkey' ||
                  key === 'formId' ||
                  key === 'api_success' ||
                  key === 'is_new' ||
                  key === 'is_updated' ||
                  key === 'duplicate_count' ||
                  key === 'duplicate_ids' ||
                  key === 'is_duplicate_of_sent' ||
                  key === 'current_data_id' ||
                  key === 'original_data_id' ||
                  key === 'lead_history_count'
                ) {
                  return null;
                }

                // Special handling for other_data - show as textarea
                if (key === 'other_data') {
                  const displayValue = editFormData[key]
                    ? typeof editFormData[key] === 'object'
                      ? JSON.stringify(editFormData[key], null, 2)
                      : editFormData[key]
                    : '';

                  return (
                    <TextField
                      key={key}
                      label={key.replace(/_/g, ' ').toUpperCase()}
                      value={displayValue}
                      onChange={(e) =>
                        handleEditFormChange(key, e.target.value)
                      }
                      fullWidth
                      size='small'
                      multiline
                      rows={4}
                      helperText='JSON format: {"key": "value"} or plain text'
                      sx={{gridColumn: 'span 2'}}
                    />
                  );
                }

                return (
                  <TextField
                    key={key}
                    label={key.replace(/_/g, ' ').toUpperCase()}
                    value={editFormData[key] || ''}
                    onChange={(e) => handleEditFormChange(key, e.target.value)}
                    fullWidth
                    size='small'
                    disabled={key === 'integration_source'}
                    helperText={
                      key === 'integration_source'
                        ? 'This field cannot be edited'
                        : ''
                    }
                  />
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant='contained'
            startIcon={<SaveIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Other Data Modal */}
      <Dialog
        open={otherDataModalOpen}
        onClose={handleCloseOtherDataModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Other Data</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              maxHeight: '400px',
              overflow: 'auto',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            {otherDataContent ? (
              typeof otherDataContent === 'object'
                ? JSON.stringify(otherDataContent, null, 2)
                : otherDataContent
            ) : 'No data available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOtherDataModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

RowEditControlGrid.propTypes = {
  dataToShow: PropTypes.array.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  pageNumber: PropTypes.number.isRequired,
  setPageNumber: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onEditCommit: PropTypes.func.isRequired,
  setPatchEdited: PropTypes.func.isRequired,
  gettingData: PropTypes.bool.isRequired,
  handleOnSelect: PropTypes.func.isRequired,
  userRole: PropTypes.array.isRequired,
  formatDate: PropTypes.func.isRequired,
  onOpenLeadHistory: PropTypes.func, // Handler for opening lead history
};
